import { MouseEvent, ReactElement, useCallback } from 'react';
import tw, { styled } from 'twin.macro';
import useControl from '@hooks/useControl';
import useControlList from '@hooks/useControlList';
import useSelectedControl from '@hooks/useSelectedControl';
import ControlPackages from '@controls/index';
import ControlPresenter from '@components/ControlPresenter';

interface ContainerProps {
    isSelected?: boolean | undefined;
}

const Container = styled.div(({ isSelected }: ContainerProps) => [
    tw`border border-transparent w-full h-full overflow-hidden`,
    isSelected ? tw`border-blue-400 ring shadow ring-blue-400 ring-opacity-30` : '',
]);
const DecoratorViewport = tw.div`flex flex-col relative`;

interface Props {
    controlId: number;
}

function InheritedControlPresenter({ controlId }: Props) {
    const { selectedControlId, selectControl } = useSelectedControl();
    const { control } = useControl(controlId);
    const { getControlsByParentId } = useControlList();

    const handleMouseUp = useCallback(
        (evt: MouseEvent) => {
            selectControl(controlId);
            evt.stopPropagation();
        },
        [selectControl, controlId],
    );

    if (!control) return null;
    if (control.properties.position.placement !== 'inherited') {
        throw new Error('Wrong presenter');
    }

    const childControls = getControlsByParentId(controlId);
    const controlPackage = ControlPackages[control.type];
    let children: ReactElement[] | undefined;

    if (childControls.length > 0) {
        children = childControls.map(child => (
            <ControlPresenter controlId={child.globalId} key={`ctl-${child.globalId}`} />
        ));
    }

    const renderedControl = controlPackage.renderDesigner(controlId, children);

    if (selectedControlId === controlId) {
        return (
            <DecoratorViewport id={`decorator-attachment-target-${controlId}`} onMouseUp={handleMouseUp}>
                <Container isSelected>{renderedControl}</Container>
            </DecoratorViewport>
        );
    }

    return (
        <DecoratorViewport>
            <Container onMouseUp={handleMouseUp}>{renderedControl}</Container>
        </DecoratorViewport>
    );
}

export default InheritedControlPresenter;
